import { z } from 'zod';

/**
 * Contratto condiviso BE↔FE per l'AI Health Check (tab "Health Check" in
 * AI Tools). Il run è persistito in `ai_health_check_runs` + risultati in
 * `ai_health_check_results`; lo stato di controllo (pausa/stop) vive nel DB
 * così sopravvive a riavvii e cluster multi-worker.
 */

export const aiHealthCheckTestTypeSchema = z.enum(['connection', 'models', 'routing', 'agents']);
export type AiHealthCheckTestType = z.infer<typeof aiHealthCheckTestTypeSchema>;

export const aiHealthCheckResultStatusSchema = z.enum(['pass', 'fail', 'warning', 'skip', 'inactive']);
export type AiHealthCheckResultStatus = z.infer<typeof aiHealthCheckResultStatusSchema>;

export const aiHealthCheckRunStatusSchema = z.enum([
  'running',
  'paused',
  'cancelling',
  'completed',
  'cancelled',
  'failed',
]);
export type AiHealthCheckRunStatus = z.infer<typeof aiHealthCheckRunStatusSchema>;

export const aiHealthCheckVerdictSchema = z.enum(['excellent', 'good', 'warning', 'critical', 'cancelled']);
export type AiHealthCheckVerdict = z.infer<typeof aiHealthCheckVerdictSchema>;

/**
 * Famiglia del provider testato. `custom` = endpoint OpenAI-compatible del
 * tenant: la colonna `provider` del risultato porta il ref `custom:<uuid>`,
 * `provider_label` il nome scelto dal tenant.
 */
export const aiHealthCheckProviderKindSchema = z.enum(['openai', 'claude', 'gemini', 'elevenlabs', 'custom']);
export type AiHealthCheckProviderKind = z.infer<typeof aiHealthCheckProviderKindSchema>;

export const aiHealthCheckAgentDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  model: z.string().nullable(),
  is_active: z.boolean(),
  kind: z.enum(['chat', 'voice']),
});
export type AiHealthCheckAgentDetail = z.infer<typeof aiHealthCheckAgentDetailSchema>;

export const aiHealthCheckRoutingLegDetailSchema = z.object({
  service_key: z.string(),
  label_it: z.string(),
  model: z.string(),
  source: z.enum(['tenant', 'platform', 'default']),
  available: z.boolean(),
});
export type AiHealthCheckRoutingLegDetail = z.infer<typeof aiHealthCheckRoutingLegDetailSchema>;

export const aiHealthCheckResultDetailsSchema = z.object({
  provider_kind: aiHealthCheckProviderKindSchema,
  provider_label: z.string(),
  error: z.string().optional(),
  latency_ms: z.number().int().nonnegative().optional(),
  available_models: z.array(z.string()).optional(),
  found: z.array(z.string()).optional(),
  missing: z.array(z.string()).optional(),
  legs: z.array(aiHealthCheckRoutingLegDetailSchema).optional(),
  agents: z.array(aiHealthCheckAgentDetailSchema).optional(),
  active: z.number().int().nonnegative().optional(),
  inactive: z.number().int().nonnegative().optional(),
});
export type AiHealthCheckResultDetails = z.infer<typeof aiHealthCheckResultDetailsSchema>;

export const aiHealthCheckResultSchema = z.object({
  id: z.string().uuid(),
  run_id: z.string().uuid(),
  provider: z.string(),
  test_type: aiHealthCheckTestTypeSchema,
  status: aiHealthCheckResultStatusSchema,
  message: z.string(),
  details: aiHealthCheckResultDetailsSchema,
  duration_ms: z.number().int().nonnegative(),
  tested_at: z.string(),
});
export type AiHealthCheckResult = z.infer<typeof aiHealthCheckResultSchema>;

export const aiHealthCheckRunSchema = z.object({
  id: z.string().uuid(),
  status: aiHealthCheckRunStatusSchema,
  started_at: z.string(),
  completed_at: z.string().nullable(),
  heartbeat_at: z.string().nullable(),
  total_providers: z.number().int().nonnegative(),
  tested_providers: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  warnings: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  verdict: aiHealthCheckVerdictSchema.nullable(),
  verdict_message: z.string().nullable(),
});
export type AiHealthCheckRun = z.infer<typeof aiHealthCheckRunSchema>;

export const aiHealthCheckRunWithResultsSchema = z.object({
  run: aiHealthCheckRunSchema,
  results: z.array(aiHealthCheckResultSchema),
});
export type AiHealthCheckRunWithResults = z.infer<typeof aiHealthCheckRunWithResultsSchema>;

export const aiHealthCheckStartResponseSchema = z.object({
  run: aiHealthCheckRunSchema,
  providers: z.array(z.object({
    ref: z.string(),
    kind: aiHealthCheckProviderKindSchema,
    label: z.string(),
    is_active: z.boolean(),
  })),
});
export type AiHealthCheckStartResponse = z.infer<typeof aiHealthCheckStartResponseSchema>;

export const aiHealthCheckHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

/** Stati "vivi" di un run: il worker sta ancora lavorando (o deve fermarsi). */
export const AI_HEALTH_CHECK_LIVE_STATUSES: readonly AiHealthCheckRunStatus[] = ['running', 'paused', 'cancelling'];
export function isAiHealthCheckRunLive(status: AiHealthCheckRunStatus): boolean {
  return AI_HEALTH_CHECK_LIVE_STATUSES.includes(status);
}
